import _Request from "@/service/request";
import { Urls } from "@/service/urls";

export interface User {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  email?: string;
  role: UserRole;
  organizationId?: number;
  organizationName?: string;
  assignedBranches?: any[];
  marketIds?: number[];  // User ruxsati bor marketlar ID lari
  branchIds?: number[];  // User ruxsati bor branchlar ID lari
  isActive: boolean;
}

export enum UserRole {
  ADMIN = 'ADMIN',
  MARKET = 'MARKET',
  MANAGER = 'MANAGER',
  EMPLOYEE = 'EMPLOYEE',
  USER = 'USER',
  SELLER = 'SELLER'
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

export class UserService {
  private req = new _Request();
  private currentUser: User | null = null;

  // Authentication
  async login(request: LoginRequest): Promise<LoginResponse> {
    try {
      // First, get the token
      const loginResponse = await this.req.post(Urls.LOGIN, request);
      const token = loginResponse.data; // Backend returns "Bearer <token>"
      
      // Store token
      localStorage.setItem('token', token);
      
      // Try to fetch user info using the token (new behavior)
      try {
        const userResponse = await this.req.get(Urls.ME);
        const user: User = userResponse.data;
        
        // Store user info
        localStorage.setItem('user', JSON.stringify(user));
        this.currentUser = user;
        
        return { token, user };
      } catch (userInfoError) {
        console.warn('Failed to fetch user info from /auth/me, falling back to old behavior:', userInfoError);
        
        // Fallback: Create a minimal user object for backward compatibility
        // You can manually set your admin user here or use the debug component
        const fallbackUser: User = {
          id: 0,
          username: request.username,
          firstName: 'User',
          lastName: 'Name',
          role: UserRole.USER, // Default role, can be changed via debug component
          isActive: true
        };
        
        // Don't store the fallback user automatically - let user set it via debug component
        console.log('Login successful with token, but no user info available. Use debug component to set user role.');
        
        return { token, user: fallbackUser };
      }
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }

  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    this.currentUser = null;
  }

  // Fetch current user info from backend (useful after page refresh)
  async fetchCurrentUser(): Promise<User | null> {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return null;
      }

      const response = await this.req.get(Urls.ME);
      const user: User = response.data;
      
      // Update stored user info
      localStorage.setItem('user', JSON.stringify(user));
      this.currentUser = user;
      
      return user;
    } catch (error) {
      console.error('Failed to fetch current user:', error);
      // Clear invalid token/user data
      this.logout();
      return null;
    }
  }

  // User info
  getCurrentUser(): User | null {
    if (this.currentUser) {
      return this.currentUser;
    }

    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        this.currentUser = JSON.parse(userStr);
        return this.currentUser;
      } catch (error) {
        console.error('Failed to parse user from localStorage:', error);
        localStorage.removeItem('user');
      }
    }

    return null;
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  // Role checking
  hasRole(role: UserRole): boolean {
    const user = this.getCurrentUser();
    return user?.role === role;
  }

  hasAnyRole(roles: UserRole[]): boolean {
    const user = this.getCurrentUser();
    return user ? roles.includes(user.role) : false;
  }

  // Role hierarchy checking (higher roles include lower role permissions)
  hasRoleOrHigher(role: UserRole): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    const roleHierarchy = {
      [UserRole.ADMIN]: 6,
      [UserRole.MARKET]: 5,
      [UserRole.MANAGER]: 4,
      [UserRole.EMPLOYEE]: 3,
      [UserRole.SELLER]: 2,
      [UserRole.USER]: 1
    };

    const userRoleLevel = roleHierarchy[user.role] || 0;
    const requiredRoleLevel = roleHierarchy[role] || 0;

    return userRoleLevel >= requiredRoleLevel;
  }

  // Admin access
  isAdmin(): boolean {
    return this.hasRole(UserRole.ADMIN);
  }

  // Market manager access
  isMarketManager(): boolean {
    return this.hasAnyRole([UserRole.ADMIN, UserRole.MARKET]);
  }

  // Branch manager access
  isBranchManager(): boolean {
    return this.hasAnyRole([UserRole.ADMIN, UserRole.MARKET, UserRole.MANAGER]);
  }

  // Get user display name
  getUserDisplayName(): string {
    const user = this.getCurrentUser();
    if (!user) return 'Unknown User';
    return `${user.firstName} ${user.lastName}`;
  }

  // Get role display name
  getRoleDisplayName(): string {
    const user = this.getCurrentUser();
    if (!user) return 'No Role';

    const roleNames = {
      [UserRole.ADMIN]: 'System Administrator',
      [UserRole.MARKET]: 'Market Manager',
      [UserRole.MANAGER]: 'Branch Manager',
      [UserRole.EMPLOYEE]: 'Employee',
      [UserRole.SELLER]: 'Seller',
      [UserRole.USER]: 'User'
    };

    return roleNames[user.role] || user.role;
  }

  // Branch access
  canAccessBranch(branchId: number): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    // Admins and market managers can access all branches
    if (this.hasAnyRole([UserRole.ADMIN, UserRole.MARKET])) {
      return true;
    }

    // Yangi branchIds fielddan foydalanish (agar mavjud bo'lsa)
    if (user.branchIds && user.branchIds.length > 0) {
      return user.branchIds.includes(branchId);
    }

    // Backward compatibility - assignedBranches dan tekshirish
    if (user.assignedBranches) {
      return user.assignedBranches.some(branch => branch.id === branchId);
    }

    return false;
  }

  // Get accessible branch IDs
  getAccessibleBranchIds(): number[] {
    const user = this.getCurrentUser();
    if (!user) return [];

    // Admins and market managers can access all branches (return empty array to indicate "all")
    if (this.hasAnyRole([UserRole.ADMIN, UserRole.MARKET])) {
      return [];
    }

    // Yangi branchIds fielddan foydalanish (agar mavjud bo'lsa)
    if (user.branchIds && user.branchIds.length > 0) {
      return user.branchIds;
    }

    // Backward compatibility - assignedBranches dan olish
    const branchIds: number[] = [];
    if (user.assignedBranches) {
      user.assignedBranches.forEach(branch => {
        if (!branchIds.includes(branch.id)) {
          branchIds.push(branch.id);
        }
      });
    }

    return branchIds;
  }

  // Market access - user marketga kirisga ruxsati bormi
  canAccessMarket(marketId: number): boolean {
    const user = this.getCurrentUser();
    if (!user) return false;

    // Adminlar barcha marketlarga kirishi mumkin
    if (this.hasRole(UserRole.ADMIN)) {
      return true;
    }

    // User marketIds ro'yxatida bor-yo'qligini tekshirish
    if (user.marketIds && user.marketIds.length > 0) {
      return user.marketIds.includes(marketId);
    }

    return false;
  }

  // User ruxsati bor marketlar ID larini olish
  getAccessibleMarketIds(): number[] {
    const user = this.getCurrentUser();
    if (!user) return [];

    // Adminlar barcha marketlarga kirishi mumkin (bo'sh array qaytarish - "hamma" ni bildiradi)
    if (this.hasRole(UserRole.ADMIN)) {
      return [];
    }

    return user.marketIds || [];
  }

  // Development/Testing helper methods
  setTestUser(user: User, token = 'test-token'): void {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    this.currentUser = user;
  }

  createTestAdminUser(): User {
    return {
      id: 1,
      username: 'admin',
      firstName: 'Test',
      lastName: 'Admin',
      email: 'admin@test.com',
      role: UserRole.ADMIN,
      organizationId: 1,
      organizationName: 'Test Organization',
      assignedBranches: [
        { id: 1, name: 'Main Branch', type: 'MAIN', status: 'ACTIVE' }
      ],
      isActive: true
    };
  }
}

// Export singleton instance
export const userService = new UserService();